<template>
    <div class="bmv-field" :class="{ 'bmv-field--has-error': !!error, 'bmv-field--disabled': disabled }">
        <label v-if="label" :for="inputId" class="bmv-field-label">
            {{ label }}
            <span v-if="required" class="bmv-field-required">*</span>
        </label>

        <div class="bmv-input-wrap">
            <i v-if="iconLeft" :class="iconLeft" class="bmv-input-icon bmv-input-icon--left"></i>
            <input
                :id="inputId"
                :value="value"
                :type="type"
                :placeholder="placeholder"
                :disabled="disabled"
                :required="required"
                :readonly="readonly"
                :autocomplete="autocomplete"
                class="bmv-input"
                :class="{
                    'bmv-input--has-icon-left': !!iconLeft,
                    'bmv-input--has-icon-right': !!iconRight
                }"
                data-testid="bmv-input"
                @input="$emit('input', $event.target.value)"
                @blur="$emit('blur', $event)"
                @focus="$emit('focus', $event)"
                @keydown.enter="$emit('enter', $event)"
            />
            <i v-if="iconRight" :class="iconRight" class="bmv-input-icon bmv-input-icon--right"></i>
        </div>

        <p v-if="error" class="bmv-field-error">
            <i class="fal fa-exclamation-circle"></i> {{ error }}
        </p>
        <p v-else-if="help" class="bmv-field-help">{{ help }}</p>
    </div>
</template>

<script>
let uid = 0

export default {
    name: 'BmvInput',
    props: {
        value: { type: [String, Number], default: '' },
        label: { type: String, default: '' },
        help: { type: String, default: '' },
        error: { type: String, default: '' },
        placeholder: { type: String, default: '' },
        type: { type: String, default: 'text' },
        disabled: { type: Boolean, default: false },
        required: { type: Boolean, default: false },
        readonly: { type: Boolean, default: false },
        autocomplete: { type: String, default: null },
        iconLeft: { type: String, default: '' },
        iconRight: { type: String, default: '' },
        id: { type: String, default: '' }
    },
    computed: {
        inputId() {
            return this.id || `bmv-input-${this._uid || 0}`
        }
    },
    created() {
        if (!this._uid) this._uid = ++uid
    }
}
</script>

<style scoped>
.bmv-field {
    display: flex;
    flex-direction: column;
    gap: var(--v2-space-2);
    font-family: var(--v2-font-sans);
}

.bmv-field-label {
    font-size: var(--v2-text-xs-size);
    line-height: 1;
    color: var(--v2-text-3);
    text-transform: uppercase;
    letter-spacing: 0.03em;
    font-weight: var(--v2-font-weight-semibold);
}

.bmv-field-required {
    color: var(--v2-danger);
    margin-left: 2px;
}

.bmv-input-wrap {
    position: relative;
    display: flex;
    align-items: center;
}

.bmv-input {
    width: 100%;
    box-sizing: border-box;
    height: 36px;
    padding: 0 var(--v2-space-3);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
    background: var(--v2-surface-0);
    color: var(--v2-text-1);
    font-family: var(--v2-font-sans);
    font-size: var(--v2-text-sm-size);
    transition: border-color var(--v2-transition-fast),
        box-shadow var(--v2-transition-fast);
    outline: none;
}

.bmv-input::placeholder {
    color: var(--v2-text-4);
}

.bmv-input:hover:not(:disabled):not(:focus) {
    border-color: var(--v2-border-strong);
}

.bmv-input:focus {
    border-color: var(--v2-primary);
    box-shadow: var(--v2-shadow-focus);
}

.bmv-input:disabled {
    background: var(--v2-surface-2);
    color: var(--v2-text-4);
}

.bmv-input--has-icon-left { padding-left: 36px; }
.bmv-input--has-icon-right { padding-right: 36px; }

.bmv-input-icon {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    color: var(--v2-text-4);
    font-size: 0.9rem;
    pointer-events: none;
}

.bmv-input-icon--left { left: var(--v2-space-3); }
.bmv-input-icon--right { right: var(--v2-space-3); }

.bmv-field-help {
    margin: 0;
    font-size: var(--v2-text-xs-size);
    color: var(--v2-text-3);
}

.bmv-field-error {
    margin: 0;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: var(--v2-text-xs-size);
    color: var(--v2-danger);
}

.bmv-field--has-error .bmv-input {
    border-color: var(--v2-danger);
}

.bmv-field--has-error .bmv-input:focus {
    box-shadow: 0 0 0 3px var(--v2-danger-soft);
}
</style>
